import React, { useState } from 'react';
import {
  Compass,
  Briefcase,
  Target,
  FileCheck2,
  GitCommit,
  FlaskConical,
  Award,
  Layers,
  PieChart,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Clock,
  Search,
  Plus,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  UserCheck,
  Zap,
  DollarSign,
  SlidersHorizontal,
  FileCode2,
  Database,
  Lightbulb,
  FileText
} from 'lucide-react';
import { AppView } from '../../types';
import {
  productOSStore,
  ProductStrategy,
  ProductPortfolioItem,
  CustomerEvidence,
  CustomerProblem,
  ProductOpportunity,
  ProductBet,
  ProductRequirementsDocument,
  ProductFeature,
  ProductExperiment,
  ProductObjective,
  ProductDecisionRecord
} from '../../services/product/productOSStore';
import { ProductBetPRDStudio } from './ProductBetPRDStudio';

interface ChiefProductOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefProductOfficerCommandCenter: React.FC<ChiefProductOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'strategy-non-goals'
    | 'portfolio-matrix'
    | 'discovery-problems'
    | 'bets-roadmaps'
    | 'prds-traceability'
    | 'features-flags'
    | 'experimentation'
    | 'plg-pricing'
    | 'okrs-decisions'
  >('overview');

  const [isStudioOpen, setIsStudioOpen] = useState<boolean>(false);
  const [selectedBetForStudio, setSelectedBetForStudio] = useState<ProductBet | null>(null);

  const strategy = productOSStore.getStrategy();
  const portfolio = productOSStore.getPortfolio();
  const evidences = productOSStore.getEvidences();
  const problems = productOSStore.getProblems();
  const opportunities = productOSStore.getOpportunities();
  const bets = productOSStore.getBets();
  const prds = productOSStore.getPRDs();
  const features = productOSStore.getFeatures();
  const experiments = productOSStore.getExperiments();
  const okrs = productOSStore.getOKRs();
  const decisions = productOSStore.getDecisions();

  const activeBetsCount = bets.filter((b) => b.status === 'IN_PROGRESS' || b.status === 'APPROVED').length;
  const validatedProblemsCount = problems.filter((p) => p.status === 'VALIDATED').length;
  const runningExperimentsCount = experiments.filter((e) => e.status === 'RUNNING').length;
  const betaFeaturesCount = features.filter((f) => f.lifecycle === 'BETA').length;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Top Banner & Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
              <Compass className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wider text-blue-700 uppercase bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-200">
                  Product Operating System
                </span>
                <span className="text-xs text-[#64748B] font-mono">P29 Enterprise</span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight">
                Chief Product Officer (CPO) Command Center
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSelectedBetForStudio(null);
                setIsStudioOpen(true);
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Nouveau Bet Produit & PRD
            </button>
            <button
              onClick={() => onNavigateToView('caio-command-center')}
              className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Zap className="w-4 h-4 text-purple-600" />
              CAIO AI OS (P28)
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto flex gap-1 border-t border-[#F1F5F9]">
          {[
            { id: 'overview', label: 'Vue Exécutive', icon: PieChart },
            { id: 'strategy-non-goals', label: 'Stratégie & Non-Goals', icon: Compass },
            { id: 'portfolio-matrix', label: 'Matrice du Portfolio', icon: Briefcase },
            { id: 'discovery-problems', label: 'Customer Discovery & Problèmes', icon: Lightbulb },
            { id: 'bets-roadmaps', label: 'Product Bets & Outcome Roadmaps', icon: Target },
            { id: 'prds-traceability', label: 'PRDs & Traçabilité Exigence', icon: FileText },
            { id: 'features-flags', label: 'Registre Features & Feature Flags', icon: GitCommit },
            { id: 'experimentation', label: 'Gouvernance Expérimentation', icon: FlaskConical },
            { id: 'plg-pricing', label: 'PLG & Recherche Prix', icon: TrendingUp },
            { id: 'okrs-decisions', label: 'OKRs & Audit Log Décisions', icon: Award }
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'border-blue-600 text-blue-700 bg-blue-50/50'
                    : 'border-transparent text-[#64748B] hover:text-[#0F172A] hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* EXECUTIVE OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Paris Produit Actifs</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{activeBetsCount}</div>
                <div className="text-xs text-blue-600 font-semibold mt-1">Outcome Driven</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Problèmes Validés</div>
                <div className="text-2xl font-black text-emerald-600 mt-1">{validatedProblemsCount}</div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">Preuves Clients Verified</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Features en Beta</div>
                <div className="text-2xl font-black text-purple-600 mt-1">{betaFeaturesCount}</div>
                <div className="text-xs text-purple-700 font-semibold mt-1">Multi-Marchés (FR/CH/BE)</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Expériences en Cours</div>
                <div className="text-2xl font-black text-amber-600 mt-1">{runningExperimentsCount}</div>
                <div className="text-xs text-amber-700 font-semibold mt-1">Guardrails Metrics Set</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">Time-to-Value Médian</div>
                <div className="text-2xl font-black text-indigo-600 mt-1">4.5s</div>
                <div className="text-xs text-indigo-700 font-semibold mt-1">Calculateur Déterministe</div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-[#E2E8F0] shadow-2xs">
                <div className="text-xs font-semibold text-[#64748B] uppercase">OKRs Produit</div>
                <div className="text-2xl font-black text-teal-600 mt-1">84%</div>
                <div className="text-xs text-teal-700 font-semibold mt-1">Taux d&apos;accomplissement</div>
              </div>
            </div>

            {/* Core Directives */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                    Principes Directeurs Produit YamooPay
                  </h2>
                  <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold rounded-full">
                    Value First
                  </span>
                </div>

                <div className="space-y-3 text-sm text-[#475569]">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">1. BUILDING SOFTWARE IS NOT THE GOAL</strong>
                    Le succès n&apos;est pas mesuré par le volume de features livrées mais par la valeur créée pour l&apos;utilisateur.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">2. EXPLICIT NON-GOALS & TRADE-OFFS</strong>
                    Chaque stratégie produit définit clairement ce que YamooPay choisit d&apos;IGNORER sciemment.
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <strong className="text-slate-900 block font-bold">3. NO GHOST FEATURES / FULL TRACEABILITY</strong>
                    Problème Client → Preuve → Pari Produit → PRD → Exigence → Métrique de Succès.
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-indigo-600" />
                    Répartition du Portfolio par Rôle Stratégique
                  </h2>
                </div>

                <div className="space-y-3">
                  {portfolio.map((item) => (
                    <div key={item.id} className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs">
                      <div>
                        <div className="font-bold text-[#0F172A] text-sm">{item.name}</div>
                        <div className="text-slate-500 mt-0.5">
                          {item.activeUsersMonthly.toLocaleString()} MAU • Retention D30: {item.retentionD30Pct}%
                        </div>
                      </div>
                      <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full font-bold">
                        {item.strategicRole}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STRATEGY & NON-GOALS */}
        {activeTab === 'strategy-non-goals' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 font-bold text-xs rounded-full border border-blue-200">
                      Version {strategy.version}
                    </span>
                    <span className="text-xs text-slate-500 font-mono">Mis à jour le {strategy.updatedAt}</span>
                  </div>
                  <h2 className="text-xl font-black text-[#0F172A] mt-2">{strategy.name}</h2>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Vision Long-Terme Produit</h3>
                    <p className="text-sm text-slate-800 leading-relaxed font-semibold">{strategy.vision}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <h3 className="text-xs font-bold uppercase text-slate-500 mb-2">Contexte Stratégique</h3>
                    <p className="text-sm text-slate-700 leading-relaxed">{strategy.strategicContext}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-200">
                    <h3 className="text-xs font-bold uppercase text-emerald-800 mb-2">Choix Stratégiques Fondamentaux</h3>
                    <ul className="list-disc pl-4 space-y-1 text-sm text-emerald-900 font-medium">
                      {strategy.coreStrategicChoices.map((choice, i) => (
                        <li key={i}>{choice}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-rose-50/50 rounded-xl border border-rose-200">
                    <h3 className="text-xs font-bold uppercase text-rose-800 mb-2">NON-GOALS ABSOLUS (YamooPay Choisit de NE PAS Faire)</h3>
                    <ul className="list-disc pl-4 space-y-1 text-sm text-rose-900 font-bold">
                      {strategy.nonGoals.map((ng, i) => (
                        <li key={i}>{ng}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PORTFOLIO MATRIX */}
        {activeTab === 'portfolio-matrix' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
              <div className="p-4 border-b border-[#E2E8F0] font-bold text-slate-800 text-sm flex justify-between items-center">
                <span>Inventaire & Matrice du Portfolio Produit ({portfolio.length})</span>
                <span className="text-xs text-slate-500 font-mono">Capacity & Lifecycle Management</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-4">Produit / Domaine</th>
                      <th className="p-4">Owner</th>
                      <th className="p-4">Rôle Stratégique</th>
                      <th className="p-4">Cycle de Vie</th>
                      <th className="p-4">Usage (MAU)</th>
                      <th className="p-4">Rétention D30</th>
                      <th className="p-4">Score Santé</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {portfolio.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4 font-bold text-slate-900">{item.name}</td>
                        <td className="p-4 text-xs font-mono">{item.owner}</td>
                        <td className="p-4">
                          <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-bold text-xs border border-indigo-200">
                            {item.strategicRole}
                          </span>
                        </td>
                        <td className="p-4 font-mono text-xs">{item.lifecycle}</td>
                        <td className="p-4 font-mono text-xs">{item.activeUsersMonthly.toLocaleString()}</td>
                        <td className="p-4 font-mono text-xs text-emerald-600 font-bold">{item.retentionD30Pct}%</td>
                        <td className="p-4">
                          <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-xs rounded-full border border-emerald-200">
                            {item.healthScore}/100
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* DISCOVERY & PROBLEMS */}
        {activeTab === 'discovery-problems' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Customer Evidence Repository</h2>
                <p className="text-sm text-slate-600 mb-4">Interviews, retours support et requêtes analytics.</p>

                <div className="space-y-3">
                  {evidences.map((ev) => (
                    <div key={ev.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">{ev.type}</span>
                        <span className="text-slate-500 font-mono">Confiance: {ev.confidence}%</span>
                      </div>
                      <p className="text-slate-800 font-medium">{ev.observation}</p>
                      <div className="text-slate-500 pt-2 border-t border-slate-200 flex justify-between">
                        <span>Source: {ev.source}</span>
                        <span>{ev.dateRecorded}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Registre des Problèmes Clients Validés</h2>
                <p className="text-sm text-slate-600 mb-4">Problèmes utilisateur formalisés sans solution prématurée.</p>

                <div className="space-y-3">
                  {problems.map((prob) => (
                    <div key={prob.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">{prob.status}</span>
                        <span className="text-slate-500 font-mono">Sévérité: {prob.severity}</span>
                      </div>
                      <p className="text-slate-900 font-bold">{prob.statement}</p>
                      <div className="text-slate-600 p-2 bg-white rounded border border-slate-200">
                        Contournement actuel: <em>{prob.currentWorkaround}</em>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BETS & ROADMAPS */}
        {activeTab === 'bets-roadmaps' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Registre des Paris Produit (Product Bets)</h2>
                  <p className="text-sm text-slate-600">Hypothèses falsifiables basées sur des résultats visés et critères d&apos;arrêt (Kill Criteria).</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedBetForStudio(null);
                    setIsStudioOpen(true);
                  }}
                  className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> Créer Pari
                </button>
              </div>

              <div className="space-y-4">
                {bets.map((bet) => (
                  <div key={bet.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/40 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 font-bold text-xs rounded-full border border-purple-200">
                          {bet.status}
                        </span>
                        <h3 className="text-base font-bold text-slate-900 mt-1">{bet.title}</h3>
                      </div>
                      <span className="text-xs font-mono text-slate-500 bg-white px-3 py-1 rounded border border-slate-200">
                        Timebox: {bet.timeboxWeeks} semaines
                      </span>
                    </div>

                    <p className="text-sm text-slate-700 font-mono bg-white p-3 rounded-lg border border-slate-200">
                      Hypothèse: &quot;{bet.hypothesis}&quot;
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div className="p-2.5 bg-emerald-50/50 rounded-lg border border-emerald-200">
                        <strong className="text-emerald-900 block font-bold">Objectif Visé:</strong>
                        <span className="text-emerald-800">{bet.targetOutcome}</span>
                      </div>
                      <div className="p-2.5 bg-rose-50/50 rounded-lg border border-rose-200">
                        <strong className="text-rose-900 block font-bold">Critères d&apos;Arrêt (Kill Criteria):</strong>
                        <span className="text-rose-800">{bet.killCriteria}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PRDS & TRACEABILITY */}
        {activeTab === 'prds-traceability' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Moteur de PRD & Traçabilité Complète</h2>
              <p className="text-sm text-slate-600 mb-6">Documents d&apos;exigences et maillage direct des objectifs jusqu&apos;aux événements d&apos;analytics.</p>

              <div className="space-y-6">
                {prds.map((prd) => (
                  <div key={prd.id} className="p-5 border border-slate-200 rounded-xl bg-slate-50/40 space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-mono text-xs font-bold bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded border border-blue-200">
                          PRD Version {prd.version}
                        </span>
                        <h3 className="text-base font-bold text-slate-900 mt-1">{prd.title}</h3>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold text-xs rounded-full border border-emerald-200">
                        Revue Sécurité: Validée
                      </span>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="font-bold text-slate-800">Objectifs Qualifiés:</div>
                      <ul className="list-disc pl-4 space-y-0.5 text-slate-700">
                        {prd.goals.map((g, i) => (
                          <li key={i}>{g}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-3 bg-white rounded-lg border border-slate-200 text-xs">
                      <div className="font-bold text-slate-800 mb-1">Événements Analytics Obligatoires:</div>
                      <div className="flex gap-2 flex-wrap">
                        {prd.analyticsEventsRequired.map((ev, i) => (
                          <span key={i} className="font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                            {ev}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FEATURES & FLAGS */}
        {activeTab === 'features-flags' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Gouvernance des Feature Flags</h2>
              <p className="text-sm text-slate-600 mb-6">Suivi du cycle de vie des fonctionnalités et dates de nettoyage imposées (Cleanup Due).</p>

              <div className="space-y-4">
                {features.map((f) => (
                  <div key={f.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 flex flex-col md:flex-row justify-between gap-4 text-xs">
                    <div>
                      <div className="font-bold text-slate-900 text-sm">{f.name}</div>
                      <div className="text-purple-700 font-mono mt-0.5">Flag Key: {f.featureFlagKey}</div>
                      <div className="text-slate-500 mt-1">
                        Scope Marchés: {f.marketScope.join(', ')} • Taux d&apos;Adoption: <strong>{f.adoptionRatePct}%</strong>
                      </div>
                    </div>

                    <div className="text-right flex flex-col justify-between">
                      <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-bold inline-block">
                        {f.lifecycle}
                      </span>
                      <div className="text-slate-500 font-mono">Clean-up Due: {f.flagCleanupDateDue}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* EXPERIMENTATION */}
        {activeTab === 'experimentation' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Plateforme d&apos;Expérimentation Produit</h2>
              <p className="text-sm text-slate-600 mb-6">A/B Testing rigoureux avec métriques gardiennes (Guardrails) et plans d&apos;analyse explicites.</p>

              <div className="space-y-4">
                {experiments.map((exp) => (
                  <div key={exp.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-3 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                        {exp.status}
                      </span>
                      <span className="font-mono text-slate-500">Démarré le {exp.startedAt}</span>
                    </div>

                    <h3 className="text-sm font-bold text-slate-900">{exp.title}</h3>
                    <p className="text-slate-700 font-mono bg-white p-2.5 rounded border border-slate-200">
                      Hypothèse: &quot;{exp.hypothesis}&quot;
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-slate-500">Métrique Principale:</span>
                        <div className="font-bold font-mono text-emerald-700">{exp.primaryMetric}</div>
                      </div>
                      <div>
                        <span className="text-slate-500">Métriques Gardiennes (Guardrails):</span>
                        <div className="font-mono text-rose-700">{exp.guardrailMetrics.join(', ')}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* OKRS & DECISIONS */}
        {activeTab === 'okrs-decisions' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Product OKRs (Outcomes Driven)</h2>
                <p className="text-sm text-slate-600 mb-4">Objectifs et résultats clés axés sur l&apos;impact utilisateur.</p>

                {okrs.map((okr) => (
                  <div key={okr.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-3 text-xs">
                    <div className="font-bold text-slate-900 text-sm">{okr.objective}</div>
                    <div className="space-y-2">
                      {okr.keyResults.map((kr, i) => (
                        <div key={i} className="p-2.5 bg-white rounded border border-slate-200">
                          <div className="text-slate-800 font-medium">{kr.statement}</div>
                          <div className="mt-1 flex justify-between font-mono text-slate-500">
                            <span>Base: {kr.baseline}</span>
                            <span>Cible: <strong>{kr.target}</strong></span>
                            <span className="text-emerald-600 font-bold">Actuel: {kr.current}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
                <h2 className="text-lg font-bold text-slate-900 mb-1">Audit Log des Décisions Produit</h2>
                <p className="text-sm text-slate-600 mb-4">Historique des arbitrages majeurs et justificatifs.</p>

                {decisions.map((dec) => (
                  <div key={dec.id} className="p-4 border border-slate-200 rounded-xl bg-slate-50/50 space-y-2 text-xs">
                    <div className="font-bold text-slate-900 text-sm">{dec.title}</div>
                    <p className="text-slate-700 font-medium p-2 bg-white rounded border border-slate-200">{dec.decision}</p>
                    <div className="text-slate-500 pt-1 flex justify-between">
                      <span>Décidé par: <strong>{dec.decidedBy}</strong></span>
                      <span>{dec.decidedAt}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Studio Modal */}
      {isStudioOpen && (
        <ProductBetPRDStudio
          bet={selectedBetForStudio}
          onClose={() => setIsStudioOpen(false)}
        />
      )}
    </div>
  );
};
